import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Trash2, BellRing, CheckCircle2, Loader2, FileText, FileDown,
} from "lucide-react";
import { invoicesApi } from "@/services/api";
import { downloadInvoicePdf } from "@/pdf/webReports";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, fmtDateTime, apiError, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">{value}</dd>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: inv, loading, error, reload } = useAsync(() => invoicesApi.getOne(id), [id]);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ payment_date: new Date().toISOString().slice(0, 10), amount: "", reference: "", method: "bank_transfer" });

  if (loading) return <div><Back /><LoadingState label="Loading invoice…" /></div>;
  if (error) return <div><Back /><ErrorState message={error} onRetry={reload} /></div>;
  if (!inv) return null;

  const items = inv.items_snapshot || [];
  const isPaid = inv.status === "paid";
  const isCancelled = inv.status === "cancelled";

  const markPaid = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await invoicesApi.update(id, {
        payment: {
          payment_date: new Date(pay.payment_date).toISOString(),
          amount: pay.amount ? Number(pay.amount) : inv.total,
          reference: pay.reference || undefined,
          method: pay.method || undefined,
        },
      });
      toast.success("Marked as paid");
      setPayOpen(false);
      await reload();
    } catch (err) { toast.error(apiError(err, "Could not record payment")); }
    finally { setBusy(false); }
  };

  const sendReminder = async () => {
    setBusy(true);
    try { await invoicesApi.remind(id); toast.success("Reminder sent"); await reload(); }
    catch (err) { toast.error(apiError(err, "Could not send reminder")); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    setBusy(true);
    try { await invoicesApi.remove(id); toast.success("Invoice deleted"); navigate("/invoices"); }
    catch (err) { toast.error(apiError(err, "Could not delete")); setBusy(false); }
  };

  const doDownloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadInvoicePdf(inv); toast.success("Invoice PDF generated"); }
    catch (err) { toast.error(apiError(err, "Could not generate PDF")); }
    finally { setPdfBusy(false); }
  };

  return (
    <div>
      <Back />
      <PageHeader
        title={inv.invoice_number}
        subtitle={inv.building_name || inv.client_name || ""}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={statusTone(inv.overdue && !isPaid ? "overdue" : inv.status)}>
              {inv.overdue && !isPaid ? `Overdue ${inv.days_overdue}d` : prettyStatus(inv.status)}
            </Badge>
            <Button variant="outline" size="sm" disabled={pdfBusy} onClick={doDownloadPdf} data-testid="invoice-download-pdf">
              {pdfBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} PDF
            </Button>
          </div>
        }
        testId="invoice-detail-header"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card className="p-5">
            <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><FileText className="h-5 w-5 text-primary" /> Line items</h3>
            {items.length === 0 ? (
              <p className="py-4 text-center text-sm text-slate-400">
                {inv.invoice_kind !== "full" ? `${prettyStatus(inv.invoice_kind)} invoice — ${inv.deposit_percent || ""}% of ${fmtMoney(inv.quote_net_total)}` : "No line breakdown on this invoice."}
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {items.map((it, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{it.component || it.description || "Item"}{it.quantity > 1 ? ` ×${it.quantity}` : ""}</p>
                      {it.description && it.component && <p className="text-sm text-slate-500">{it.description}</p>}
                    </div>
                    <span className="shrink-0 font-semibold text-slate-800">{fmtMoney(Number(it.cost || 0) * Number(it.quantity || 1))}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {(inv.reminders || []).length > 0 && (
            <Card className="p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><BellRing className="h-5 w-5 text-slate-400" /> Reminder history</h3>
              <div className="space-y-2">
                {inv.reminders.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{r.channel === "email" ? "Emailed" : "Logged"} {r.to ? `· ${r.to}` : ""}</span>
                    <span className="text-slate-400">{fmtDateTime(r.at)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="p-5">
            <h3 className="mb-3 font-semibold text-slate-800">Summary</h3>
            <dl className="space-y-2 text-sm">
              <Row label="Amount" value={fmtMoney(inv.amount)} />
              <Row label="VAT" value={fmtMoney(inv.vat)} />
              <div className="flex items-center justify-between border-t border-slate-100 pt-2">
                <dt className="font-semibold text-slate-800">Total</dt>
                <dd className="text-lg font-bold text-slate-900" data-testid="invoice-total">{fmtMoney(inv.total)}</dd>
              </div>
            </dl>
          </Card>

          <Card className="p-5">
            <h3 className="mb-3 font-semibold text-slate-800">Details</h3>
            <div className="space-y-2 text-sm">
              <Row label="Issued" value={fmtDate(inv.invoice_date)} />
              <Row label="Due" value={fmtDate(inv.due_date)} />
              <Row label="Terms" value={`${inv.payment_terms_days} days`} />
              <Row label="Type" value={<span className="capitalize">{inv.invoice_type}</span>} />
              {inv.client_email && <Row label="Email" value={inv.client_email} />}
              {inv.building_id && <Row label="Building" value={<Link to={`/buildings/${inv.building_id}`} className="text-primary hover:underline">{inv.building_name || "View"}</Link>} />}
              {inv.quote_id && <Row label="Quote" value={<Link to={`/quotes/${inv.quote_id}`} className="text-primary hover:underline">View quote</Link>} />}
            </div>
          </Card>

          {isPaid ? (
            <Card className="border-emerald-200 bg-emerald-50 p-5">
              <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-semibold">Paid</span></div>
              <div className="mt-2 space-y-1 text-sm text-emerald-800">
                {inv.payment_date && <p>On {fmtDate(inv.payment_date)}</p>}
                {inv.payment_amount != null && <p>{fmtMoney(inv.payment_amount)}{inv.payment_method ? ` · ${prettyStatus(inv.payment_method)}` : ""}</p>}
                {inv.payment_reference && <p>Ref: {inv.payment_reference}</p>}
              </div>
            </Card>
          ) : (
            <Card className="p-5 space-y-2">
              {!isCancelled && (
                <>
                  <Button className="w-full" disabled={busy} onClick={() => setPayOpen(true)} data-testid="invoice-mark-paid-btn"><CheckCircle2 className="mr-2 h-4 w-4" /> Mark as paid</Button>
                  <Button className="w-full" variant="outline" disabled={busy} onClick={sendReminder} data-testid="invoice-remind-btn">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BellRing className="mr-2 h-4 w-4" />}
                    Send reminder{inv.reminder_count ? ` (${inv.reminder_count} sent)` : ""}
                  </Button>
                </>
              )}
              <Button className="w-full text-red-600 hover:text-red-700" variant="ghost" disabled={busy} onClick={doDelete} data-testid="invoice-delete-btn"><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Record payment</DialogTitle><DialogDescription>Mark this invoice as paid.</DialogDescription></DialogHeader>
          <form onSubmit={markPaid} className="space-y-4">
            <div className="space-y-2"><Label>Payment date</Label><Input type="date" value={pay.payment_date} onChange={(e) => setPay((p) => ({ ...p, payment_date: e.target.value }))} data-testid="payment-date-input" /></div>
            <div className="space-y-2"><Label>Amount (£)</Label><Input type="number" step="0.01" min="0" placeholder={String(inv.total)} value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} data-testid="payment-amount-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Method</Label>
                <Select value={pay.method} onValueChange={(v) => setPay((p) => ({ ...p, method: v }))}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Reference</Label><Input value={pay.reference} onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))} placeholder="Optional" data-testid="payment-ref-input" /></div>
            </div>
            <DialogFooter><Button type="submit" disabled={busy} data-testid="payment-save-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save payment</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Back() {
  return (
    <Link to="/invoices" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
      <ArrowLeft className="h-4 w-4" /> Invoices
    </Link>
  );
}
